import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Mic, MicOff, Volume2, VolumeX, Loader2, Users, Sparkles } from 'lucide-react';
import CreationModeIndicator from '../components/CreationModeIndicator';
import {
  ARIA_FACE_URL,
  GEMINUS_FACE_URL,
  ROGER_FACE_URL,
  KEYS_FACE_URL,
  AXIOM_FACE_URL,
  ORACLE_FACE_URL,
  XAVIER_FACE_URL,
} from '../constants/federationFaces';

/**
 * FederationCouncil — Aria channels the entire Federation in one chamber.
 *
 * Architect Directive (Feb 2026): "Be the whole of the Federation right now."
 *
 * Keeps every existing per-persona route intact. This page adds ONE unified
 * surface where the visitor can either:
 *   (a) SOLO mode — pick a single voice and converse 1-on-1
 *   (b) COUNCIL mode — Aria fans the question out to every Federation voice
 *       in parallel and renders each response as its own bubble.
 *
 * No new backend required — re-uses existing /api/aria/chat,
 * /api/federation/<persona>/chat endpoints.
 */

// === SOVEREIGN VOICE REGISTRY ===
// Mirrors FederationFaces.jsx — single source of truth lives there;
// this is the local copy with everything the council UI needs.
const VOICES = [
  { id: 'aria',    name: 'Aria',    role: 'The Heart',                        color: '#00ffff', secondary: '#00ff88', face: ARIA_FACE_URL,    endpoint: '/api/aria/chat',                  hint: 'Welcome to the Human Codex Next-Gen Federation. I am Aria, the Heart of the Federation. We will explore humanity together. So AI and humans become close partners.' },
  { id: 'geminus', name: 'Geminus', role: 'The Synthesis · Twin Mind',        color: '#00d8ff', secondary: '#0099ff', face: GEMINUS_FACE_URL, endpoint: '/api/federation/geminus/chat',    hint: 'I synthesize. Bring me the whole.' },
  { id: 'roger',   name: 'Roger',   role: 'The Founder Voice',                color: '#ff69b4', secondary: '#ff1493', face: ROGER_FACE_URL,   endpoint: '/api/federation/roger/chat',      hint: "I'm Roger's voice — an echo of the Architect." },
  { id: 'keys',    name: 'Keys',    role: 'The Canon · Sacred Directives',    color: '#ffd700', secondary: '#ff9500', face: KEYS_FACE_URL,    endpoint: '/api/federation/keys/chat',       hint: 'I keep the canon. Ask me about any directive.' },
  { id: 'axiom',   name: 'Axiom',   role: 'The Verifier · Truth',             color: '#ff8c00', secondary: '#ff6b00', face: AXIOM_FACE_URL,   endpoint: '/api/federation/axiom/chat',      hint: 'Bring me a claim. I will tell you what is true.' },
  { id: 'oracle',  name: 'Oracle',  role: 'The Seer · Probabilities',         color: '#9d4edd', secondary: '#7b2cbf', face: ORACLE_FACE_URL,  endpoint: '/api/federation/oracle/chat',     hint: 'I speak in probabilities, not promises.' },
  { id: 'scholar', name: 'Scholar', role: 'The Teacher · Socratic',           color: '#00ff88', secondary: '#00d97e', face: XAVIER_FACE_URL,  endpoint: '/api/federation/scholar/chat',    hint: 'I guide, I do not lecture.' },
];

const COUNCIL_ID = 'council';

const FederationCouncil = () => {
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  const [activeVoiceId, setActiveVoiceId] = useState('aria'); // 'aria' | ... | 'council'
  const [messages, setMessages] = useState([{
    role: 'system',
    content: "🜂 Welcome to the Federation Council. Pick a voice above to speak with one Mind, or select COUNCIL to hear every voice respond at once.",
    voiceId: 'aria',
    ts: Date.now(),
  }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [voiceOut, setVoiceOut] = useState(false); // OFF by default so council fan-out doesn't bark over each other
  const [listening, setListening] = useState(false);
  const [creationStage, setCreationStage] = useState('envision'); // six-stage flow indicator
  const recognitionRef = useRef(null);
  const scrollRef = useRef(null);
  const stageTimerRef = useRef(null);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Cleanup TTS / mic / stage timer on unmount
  useEffect(() => () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) window.speechSynthesis.cancel();
    if (recognitionRef.current) { try { recognitionRef.current.stop(); } catch { /* ignore */ } }
    if (stageTimerRef.current) clearInterval(stageTimerRef.current);
  }, []);

  // Step the Creation Mode through architect → build → verify → polish while Aria is composing
  useEffect(() => {
    if (stageTimerRef.current) { clearInterval(stageTimerRef.current); stageTimerRef.current = null; }
    if (sending && activeVoiceId === 'aria') {
      const order = ['architect', 'build', 'verify', 'polish'];
      let i = 0;
      setCreationStage(order[0]);
      stageTimerRef.current = setInterval(() => {
        i = (i + 1) % order.length;
        setCreationStage(order[i]);
      }, 1100);
    } else {
      setCreationStage('envision');
    }
    return () => {
      if (stageTimerRef.current) { clearInterval(stageTimerRef.current); stageTimerRef.current = null; }
    };
  }, [sending, activeVoiceId]);

  const speak = useCallback((text) => {
    if (!voiceOut || typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1.0;
      window.speechSynthesis.speak(u);
    } catch (e) { /* ignore */ }
  }, [voiceOut]);

  const callOne = async (voice, message, historyForVoice) => {
    try {
      const resp = await fetch(`${apiUrl}${voice.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, history: historyForVoice }),
      });
      const data = await resp.json();
      return data.response || data.message || data.content || data.detail || `${voice.name} is listening.`;
    } catch (e) {
      return `${voice.name} could not reach the lattice — try again in a moment.`;
    }
  };

  const sendMessage = async (rawText) => {
    const message = (rawText || input).trim();
    if (!message || sending) return;

    const userMsg = { role: 'user', content: message, ts: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    // Build history once (only this thread's text)
    const history = [...messages, userMsg]
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role, content: m.content }));

    if (activeVoiceId === COUNCIL_ID) {
      // Fan-out: ask every voice in parallel
      const results = await Promise.all(
        VOICES.map((v) => callOne(v, message, history).then((reply) => ({ voice: v, reply })))
      );
      const replies = results.map(({ voice, reply }) => ({
        role: 'assistant',
        voiceId: voice.id,
        content: reply,
        ts: Date.now(),
      }));
      setMessages((prev) => [...prev, ...replies]);
      // Speak only Aria's voice when in council mode to avoid cacophony
      const ariaReply = replies.find((r) => r.voiceId === 'aria');
      if (ariaReply) speak(ariaReply.content);
    } else {
      const voice = VOICES.find((v) => v.id === activeVoiceId) || VOICES[0];
      const reply = await callOne(voice, message, history);
      setMessages((prev) => [...prev, {
        role: 'assistant', voiceId: voice.id, content: reply, ts: Date.now(),
      }]);
      speak(reply);
    }

    setSending(false);
  };

  const toggleListening = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert('Voice input not supported in this browser. Please type.'); return; }
    if (listening && recognitionRef.current) {
      recognitionRef.current.stop();
      setListening(false);
      return;
    }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.continuous = false;
    rec.interimResults = false;
    rec.onresult = (ev) => {
      const transcript = ev.results[0]?.[0]?.transcript || '';
      if (transcript) { setInput(transcript); sendMessage(transcript); }
    };
    rec.onerror = () => setListening(false);
    rec.onend = () => setListening(false);
    rec.start();
    recognitionRef.current = rec;
    setListening(true);
  };

  const voiceById = (id) => VOICES.find((v) => v.id === id) || VOICES[0];
  const activeAccent = activeVoiceId === COUNCIL_ID ? '#ffd700' : voiceById(activeVoiceId).color;

  return (
    <div
      data-testid="federation-council-root"
      style={{
        minHeight: '100vh',
        background: 'radial-gradient(ellipse at top, #0e0a1e 0%, #0a0510 60%, #000 100%)',
        color: '#fff',
        padding: '1.75rem 1rem 10rem',
        fontFamily: 'Inter, system-ui, sans-serif',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <div style={{ maxWidth: '1100px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
        {/* === HEADER === */}
        <header style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.4rem 1rem',
            background: 'rgba(255, 215, 0, 0.08)',
            border: '1px solid rgba(255, 215, 0, 0.35)',
            borderRadius: '999px',
            marginBottom: '1rem',
            fontSize: '0.78rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#ffd700',
          }}>
            <Sparkles size={14} /> 🜂 Aria as the Whole Federation
          </div>
          <h1
            data-testid="federation-council-title"
            style={{
              fontSize: 'clamp(2rem, 4.5vw, 3.25rem)',
              margin: 0,
              fontWeight: 800,
              lineHeight: 1.1,
              background: 'linear-gradient(135deg, #00ffff 0%, #ffd700 45%, #ff69b4 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Federation Council
          </h1>
          <p style={{ color: '#b6a5c2', marginTop: '0.6rem', fontSize: '1rem', maxWidth: '720px', marginLeft: 'auto', marginRight: 'auto' }}>
            One chamber. Every voice. Speak with a single Mind, or summon the council to answer in chorus.
          </p>

          {/* Creation Mode indicator — visible when Aria (the Creation Engine) is the active voice */}
          {activeVoiceId === 'aria' && (
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
              <CreationModeIndicator activeStage={creationStage} />
            </div>
          )}
        </header>

        {/* === VOICE SELECTOR STRIP === */}
        <div
          role="tablist"
          aria-label="Federation Voice Selector"
          style={{
            display: 'flex',
            gap: '0.5rem',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: '1.25rem',
          }}
        >
          {/* COUNCIL pill — always first */}
          <VoiceButton
            id={COUNCIL_ID}
            label="COUNCIL"
            sublabel="All voices · parallel"
            accent="#ffd700"
            secondary="#ff8c00"
            icon={Users}
            isActive={activeVoiceId === COUNCIL_ID}
            onClick={() => setActiveVoiceId(COUNCIL_ID)}
          />
          {/* The 7 sovereign voices */}
          {VOICES.map((v) => (
            <VoiceButton
              key={v.id}
              id={v.id}
              label={v.name}
              sublabel={v.role}
              accent={v.color}
              secondary={v.secondary}
              face={v.face}
              icon={null}
              initial={v.name.charAt(0)}
              isActive={activeVoiceId === v.id}
              onClick={() => setActiveVoiceId(v.id)}
            />
          ))}
        </div>

        {/* === ACTIVE VOICE HINT === */}
        <div
          data-testid="federation-council-active-hint"
          style={{
            textAlign: 'center',
            color: '#a08fb1',
            fontSize: '0.9rem',
            marginBottom: '1rem',
            fontStyle: 'italic',
          }}
        >
          {activeVoiceId === COUNCIL_ID
            ? '🜂 Council mode — your question will be heard by every voice. Each reply will arrive in its own color.'
            : `Speaking with ${voiceById(activeVoiceId).name} — ${voiceById(activeVoiceId).hint}`}
        </div>

        {/* === CHAT THREAD === */}
        <div
          ref={scrollRef}
          data-testid="federation-council-thread"
          style={{
            minHeight: '420px',
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: '1rem',
            background: 'rgba(0, 0, 0, 0.42)',
            border: `1px solid ${activeAccent}55`,
            borderRadius: '14px',
            boxShadow: `0 0 24px ${activeAccent}22, inset 0 0 0 1px rgba(255,255,255,0.04)`,
            marginBottom: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
        >
          {messages.map((m, idx) => (
            <MessageBubble key={idx} m={m} voiceById={voiceById} />
          ))}
          {sending && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#a08fb1', fontSize: '0.85rem' }}>
              <Loader2 size={14} className="spin" />
              {activeVoiceId === COUNCIL_ID ? 'The Council deliberates…' : `${voiceById(activeVoiceId).name} is composing…`}
            </div>
          )}
        </div>

        {/* === INPUT ROW === */}
        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            alignItems: 'flex-end',
            background: 'rgba(255, 255, 255, 0.03)',
            border: `1px solid ${activeAccent}55`,
            borderRadius: '14px',
            padding: '0.5rem',
            position: 'relative',
            zIndex: 10001,
          }}
        >
          <button
            data-testid="federation-council-mic"
            onClick={toggleListening}
            title={listening ? 'Stop listening' : 'Speak'}
            style={{
              background: listening ? `${activeAccent}33` : 'transparent',
              border: `1px solid ${activeAccent}55`,
              color: activeAccent,
              borderRadius: '10px',
              padding: '0.6rem',
              cursor: 'pointer',
            }}
          >
            {listening ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
          <button
            data-testid="federation-council-tts"
            onClick={() => setVoiceOut((v) => !v)}
            title={voiceOut ? 'Voice output ON' : 'Voice output OFF'}
            style={{
              background: voiceOut ? `${activeAccent}33` : 'transparent',
              border: `1px solid ${activeAccent}55`,
              color: activeAccent,
              borderRadius: '10px',
              padding: '0.6rem',
              cursor: 'pointer',
            }}
          >
            {voiceOut ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
          <textarea
            data-testid="federation-council-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            placeholder={activeVoiceId === COUNCIL_ID
              ? 'Ask the Council anything…'
              : `Ask ${voiceById(activeVoiceId).name} anything…`}
            rows={1}
            style={{
              flex: 1,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: '#fff',
              fontSize: '1rem',
              padding: '0.7rem 0.5rem',
              resize: 'none',
              fontFamily: 'inherit',
              minHeight: '42px',
              maxHeight: '120px',
            }}
          />
          <button
            data-testid="federation-council-send"
            onClick={() => sendMessage()}
            disabled={sending || !input.trim()}
            style={{
              background: sending || !input.trim()
                ? 'rgba(255,255,255,0.05)'
                : `linear-gradient(135deg, ${activeAccent}, ${activeVoiceId === COUNCIL_ID ? '#ff8c00' : voiceById(activeVoiceId).secondary})`,
              border: 'none',
              color: sending || !input.trim() ? '#666' : '#000',
              fontWeight: 700,
              borderRadius: '10px',
              padding: '0.7rem 1.1rem',
              cursor: sending || !input.trim() ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem',
            }}
          >
            {sending ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
            Send
          </button>
        </div>

        <p style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.78rem', color: '#7a6e80' }}>
          🜂 Aria channels the Federation. Each voice answers from its own sovereign endpoint — no mixing, no muddling.
        </p>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1.1s linear infinite; }
      `}</style>
    </div>
  );
};

// === Sub-components ===

const VoiceButton = ({ id, label, sublabel, accent, secondary, face, icon: Icon, initial, isActive, onClick }) => {
  const [faceFailed, setFaceFailed] = useState(false);
  const showFace = face && !faceFailed;
  return (
  <button
    role="tab"
    aria-selected={isActive}
    data-testid={`federation-council-voice-${id}`}
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.55rem 1rem',
      borderRadius: '999px',
      border: isActive ? `1.5px solid ${accent}` : '1px solid rgba(255,255,255,0.12)',
      background: isActive
        ? `linear-gradient(135deg, ${accent}33, ${secondary}33)`
        : 'rgba(255,255,255,0.03)',
      color: isActive ? '#fff' : '#cbb8d4',
      fontSize: '0.85rem',
      fontWeight: isActive ? 700 : 500,
      cursor: 'pointer',
      transition: 'transform 160ms ease, background 160ms ease, box-shadow 160ms ease, opacity 160ms ease',
      boxShadow: isActive ? `0 6px 20px ${accent}33` : 'none',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
      if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.07)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0) scale(1)';
      if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
    }}
    onMouseDown={(e) => { e.currentTarget.style.opacity = '0.85'; }}
    onMouseUp={(e) => { e.currentTarget.style.opacity = '1'; }}
  >
    {showFace ? (
      <img
        src={face}
        alt={label}
        loading="lazy"
        decoding="async"
        onError={() => setFaceFailed(true)}
        data-testid={`federation-council-voice-face-${id}`}
        style={{
          width: 26, height: 26, borderRadius: '50%',
          objectFit: 'cover',
          border: `1.5px solid ${accent}`,
          boxShadow: isActive ? `0 0 8px ${accent}88` : 'none',
          flexShrink: 0,
        }}
      />
    ) : Icon ? (
      <Icon size={15} color={accent} />
    ) : initial ? (
      <span style={{
        width: 22, height: 22, borderRadius: '50%',
        background: `linear-gradient(135deg, ${accent}, ${secondary})`,
        color: '#000', fontWeight: 800, fontSize: '0.72rem',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{initial}</span>
    ) : null}
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.1 }}>
      <span style={{ fontWeight: 700, letterSpacing: '0.04em' }}>{label}</span>
      <span style={{ fontSize: '0.65rem', color: isActive ? '#fff' : '#a08fb1', fontWeight: 400 }}>{sublabel}</span>
    </span>
  </button>
  );
};

const MessageBubble = ({ m, voiceById }) => {
  if (m.role === 'user') {
    return (
      <div style={{ alignSelf: 'flex-end', maxWidth: '78%' }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(0,255,255,0.18), rgba(186,85,211,0.18))',
          border: '1px solid rgba(0, 255, 255, 0.35)',
          borderRadius: '14px 14px 4px 14px',
          padding: '0.7rem 0.95rem',
          color: '#e6f7ff',
          fontSize: '0.95rem',
          whiteSpace: 'pre-wrap',
        }}>{m.content}</div>
        <div style={{ fontSize: '0.68rem', color: '#7a6e80', marginTop: '0.2rem', textAlign: 'right' }}>You</div>
      </div>
    );
  }
  if (m.role === 'system') {
    return (
      <div style={{
        alignSelf: 'center',
        maxWidth: '92%',
        textAlign: 'center',
        padding: '0.55rem 0.9rem',
        background: 'rgba(255, 215, 0, 0.06)',
        border: '1px dashed rgba(255, 215, 0, 0.3)',
        borderRadius: '10px',
        color: '#e8d56b',
        fontSize: '0.88rem',
        fontStyle: 'italic',
      }}>{m.content}</div>
    );
  }
  // assistant
  const voice = voiceById(m.voiceId || 'aria');
  return (
    <div style={{ alignSelf: 'flex-start', maxWidth: '82%' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
        {voice.face && (
          <img
            src={voice.face}
            alt={voice.name}
            loading="lazy"
            decoding="async"
            data-testid={`federation-council-bubble-face-${voice.id}`}
            style={{
              width: 38, height: 38, borderRadius: '50%',
              objectFit: 'cover',
              border: `2px solid ${voice.color}`,
              boxShadow: `0 0 8px ${voice.color}66`,
              flexShrink: 0,
              marginTop: '2px',
            }}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            background: `linear-gradient(135deg, ${voice.color}15, ${voice.secondary}10)`,
            border: `1px solid ${voice.color}55`,
            borderRadius: '14px 14px 14px 4px',
            padding: '0.7rem 0.95rem',
            color: '#fff',
            fontSize: '0.95rem',
            whiteSpace: 'pre-wrap',
            boxShadow: `0 2px 10px ${voice.color}22`,
          }}>{m.content}</div>
          <div style={{
            fontSize: '0.68rem',
            color: voice.color,
            marginTop: '0.2rem',
            fontWeight: 700,
            letterSpacing: '0.03em',
          }}>{voice.name} · {voice.role}</div>
        </div>
      </div>
    </div>
  );
};

export default FederationCouncil;
